import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  AlertTitle,
  Button,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import { paymentType, saleOrigin as saleOriginEnum, resolveChannelPrice } from '../../../../utils/Enums';
import { formatMoney } from '../../../../utils/currency';

import { CURRENCIES, DEFAULT_CURRENCY } from '../../../../utils/currency';
const dashboardSaleOrigins = saleOriginEnum;

const paymentIcons = {
  1: PaymentsOutlinedIcon,
  2: CreditCardOutlinedIcon,
  3: CreditCardOutlinedIcon,
  4: QrCode2Icon,
  5: DeliveryDiningOutlinedIcon,
  6: AccountBalanceOutlinedIcon,
};

// Display order of the payment options, regardless of how the catalog lists them.
const paymentOrder = [2, 3, 1, 6, 4, 5];

const SectionTitle = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: 1.5 }}>
    {children}
  </Typography>
);

// Ícono de moneda para los botones: el símbolo de la moneda dentro de un círculo.
const CurrencyBadge = ({ code }) => (
  <Box
    component="span"
    sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 28, height: 20, px: 0.5, borderRadius: 10, bgcolor: 'action.selected', fontSize: 11, fontWeight: 700, lineHeight: 1 }}
  >
    {CURRENCIES[code]?.symbol || code}
  </Box>
);

const StepSummary = ({
  currency = DEFAULT_CURRENCY,
  items,
  paymentMethod,
  setPaymentMethod,
  saleOrigin,
  setSaleOrigin,
  lockOrigin = false,
  storeCreditAmount,
  storeCreditError,
  storeCreditLoading,
  amountDue,
  paymentCurrency = currency,
  paymentDue = amountDue,
  ticketRate,
  canChoosePaymentCurrency = false,
  mpQrSelected = false,
  onPaymentCurrencyChange,
  cardViaPos = false,
  paymentNotice = null,
  onDismissNotice,
  unbilledPayments = [],
  onUseUnbilled,
  splitAvailable = false,
  splitMode = false,
  onToggleSplit,
  splitPanel = null,
}) => {
  const saleTotal = items.reduce((sum, item) => {
    const unit = item.unitPrice || item.price || 0;
    const discount = item.discountAmount || 0;
    return sum + (unit - discount) * item.qty;
  }, 0);
  const filteredPaymentTypes = paymentType.filter(({ origin }) =>
    origin.includes(saleOrigin)
  );
  const paymentOptions = filteredPaymentTypes
    // With an integrated POS, Crédito/Débito collapse into one "Tarjeta" option: the POS
    // response decides which one it was. It keeps id 2 so the existing card flow applies.
    .filter(({ id }) => !(cardViaPos && id === 3))
    .map((option) => (cardViaPos && option.id === 2 ? { ...option, label: 'Tarjeta' } : option))
    .sort((x, y) => paymentOrder.indexOf(x.id) - paymentOrder.indexOf(y.id));
  const hasChannelPriceOverride = items.some((item) => {
    const channelPrice = resolveChannelPrice(item, saleOrigin);
    return channelPrice != null && item.price != null && channelPrice !== item.price;
  });
  const handleSaleOriginChange = (event) => {
    setSaleOrigin(Number(event.target.value));
    setPaymentMethod('');
  };

  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        Resumen de la venta:
      </Typography>

      <Box sx={{ maxHeight: 140, overflowY: 'auto' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">P. Unitario</TableCell>
              <TableCell align="right">Desc.</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const unitPrice = item.unitPrice || item.price || 0;
              const discount = item.discountAmount || 0;
              const discountedUnitPrice = unitPrice - discount;
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{item.qty}</TableCell>
                  <TableCell align="right">{formatMoney(unitPrice, currency)}</TableCell>
                  <TableCell align="right">
                    {discount > 0 ? `-${formatMoney(discount, currency)}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(discountedUnitPrice * item.qty, currency)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box mt={1} textAlign="right">
        <Typography variant="body2">
          Subtotal: {formatMoney(saleTotal, currency)}
        </Typography>
        {storeCreditAmount > 0 && <Typography variant="body2" color="success.dark">Saldo de NC aplicado: −{formatMoney(storeCreditAmount, currency)}</Typography>}
        <Typography variant="h6">
          Total: {formatMoney(amountDue, currency)}
        </Typography>
      </Box>

      {!lockOrigin && <Box sx={{ mt: 2.5 }}>
        <SectionTitle>Origen de la venta</SectionTitle>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          color="primary"
          value={saleOrigin}
          onChange={(_, value) => { if (value != null) handleSaleOriginChange({ target: { value } }); }}
          aria-label="Origen de la venta"
        >
          {dashboardSaleOrigins.map(({ id, label }) => (
            <ToggleButton key={id} value={id} sx={{ textTransform: 'none', py: 1 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {hasChannelPriceOverride && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            Los precios se actualizaron según el canal seleccionado.
          </Typography>
        )}
      </Box>}

      {!splitMode && unbilledPayments.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2.5 }}>
          <AlertTitle>Cobro aprobado sin facturar</AlertTitle>
          {unbilledPayments.map((payment) => {
            const amount = Number(payment.amount);
            // El cobro se hizo en una moneda (código ISO numérico): solo sirve si es la de esta venta.
            const chargedCurrency = Object.values(CURRENCIES).find((c) => c.isoNumeric === payment.currency)?.code || DEFAULT_CURRENCY;
            const matches = chargedCurrency === paymentCurrency && Math.abs(amount - paymentDue) <= 0.001;
            const minutes = Math.max(0, Math.round((Date.now() - new Date(payment.createdAt).getTime()) / 60000));
            return (
              <Box key={payment.transactionId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="body2">
                  {formatMoney(amount, chargedCurrency)}
                  {payment.cardBrand ? ` · ${payment.cardBrand}` : ''}
                  {payment.cardLast4 ? ` ****${payment.cardLast4}` : ''}
                  {` · hace ${minutes} min`}
                  {!matches ? ' · no coincide con el total de esta venta' : ''}
                </Typography>
                {matches && onUseUnbilled && (
                  <Button size="small" variant="outlined" color="inherit" onClick={() => onUseUnbilled(payment)}>
                    Usar este cobro
                  </Button>
                )}
              </Box>
            );
          })}
        </Alert>
      )}

      {paymentNotice && (
        <Alert severity={paymentNotice.severity || 'error'} onClose={onDismissNotice} sx={{ mt: 2.5 }}>
          {paymentNotice.title && <AlertTitle>{paymentNotice.title}</AlertTitle>}
          {paymentNotice.message}
        </Alert>
      )}

      {splitAvailable && (
        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={onToggleSplit} data-testid="split-toggle">
            {splitMode ? 'Pagar con un solo medio' : 'Dividir el pago entre varios medios'}
          </Button>
        </Box>
      )}

      {splitMode && splitPanel}

      {!splitMode && <Box sx={{ mt: 2.5 }}>
        <SectionTitle>{storeCreditAmount > 0 ? `Método para cobrar ${formatMoney(amountDue, currency)}` : 'Método de pago'}</SectionTitle>
        <Box
          role="radiogroup"
          aria-label="Método de pago"
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}
        >
          {paymentOptions.map(({ id, label }) => {
            const Icon = paymentIcons[id];
            const selected = paymentMethod === id.toString();
            return (
              <ToggleButton
                key={id}
                value={id.toString()}
                selected={selected}
                role="radio"
                aria-checked={selected}
                onChange={() => setPaymentMethod(id.toString())}
                color="primary"
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  gap: 1.25,
                  py: 1.25,
                  px: 1.5,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: selected ? 'primary.main' : 'divider',
                }}
              >
                {Icon && <Icon fontSize="small" />}
                <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>{label}</Typography>
              </ToggleButton>
            );
          })}
        </Box>
      </Box>}

      {!splitMode && canChoosePaymentCurrency && (
        <Box sx={{ mt: 2.5 }}>
          <SectionTitle>Moneda de cobro</SectionTitle>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={paymentCurrency}
            onChange={(_, value) => value && onPaymentCurrencyChange?.(value)}
            aria-label="Moneda de cobro"
          >
            <ToggleButton value={currency} sx={{ textTransform: 'none', gap: 1 }}><CurrencyBadge code={currency} />{CURRENCIES[currency]?.label || currency}</ToggleButton>
            <ToggleButton value={DEFAULT_CURRENCY} sx={{ textTransform: 'none', gap: 1 }}><CurrencyBadge code={DEFAULT_CURRENCY} />{CURRENCIES[DEFAULT_CURRENCY].label}</ToggleButton>
          </ToggleButtonGroup>
          {paymentCurrency !== currency && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {`Se cobra ${formatMoney(paymentDue, paymentCurrency)} (${formatMoney(amountDue, currency)} × ${formatMoney(ticketRate, DEFAULT_CURRENCY, { decimals: 3 })}). La factura sale en ${CURRENCIES[currency]?.label || currency}.`}
            </Typography>
          )}
        </Box>
      )}
      {!splitMode && mpQrSelected && currency !== DEFAULT_CURRENCY && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {`Mercado Pago cobra en pesos: se cobra ${formatMoney(paymentDue, DEFAULT_CURRENCY)} (${formatMoney(amountDue, currency)} × ${formatMoney(ticketRate, DEFAULT_CURRENCY, { decimals: 3 })}).`}
        </Typography>
      )}

      {storeCreditLoading && (
        <Typography sx={{ mt: 1.5 }} color="text.secondary" variant="body2">
          Validando saldo de la nota de crédito…
        </Typography>
      )}
      {storeCreditError && (
        <Typography sx={{ mt: 1.5 }} color="error" variant="body2">
          No se pudo aplicar el saldo de la nota de crédito: {storeCreditError}
        </Typography>
      )}
    </>
  );
};

export default StepSummary;
